/**
 * Form drawer (Phase 8 of pb-section-properties overhaul).
 *
 * The form-builder body is the largest surface in the panel for form
 * sections. Pre-overhaul it was appended after the decorative-SVG list with
 * no header, no separation, no visual story. Phase 8 gives it an explicit
 * home: a content-row inspector entry that opens the FormBuilder inside its
 * own drawer with the drawer's title bar acting as the section header.
 *
 * Why this is its own module instead of an inline call in property-panel:
 *
 *   1. **Lifecycle**. FormBuilder opens a `store.beginTransaction(...)` on
 *      certain edits and commits on its own `destroy()`. The drawer must
 *      destroy the FormBuilder when it closes / swaps so any in-flight
 *      transaction commits before the drawer body is wiped. Returning a
 *      teardown that drawer.js stores via `bodyCleanup` is the right hook —
 *      the inline call had no equivalent and leaked the instance until
 *      `_destroyFormBuilder` was called from elsewhere (back-to-design,
 *      panel teardown, etc.), stretching the transaction far longer than
 *      necessary.
 *
 *   2. **Encapsulation**. The FormBuilder reference lives in this module's
 *      closure rather than on `this._formBuilder` (the property-panel field
 *      that the now-removed v1 inline path used to assign). Self-contained
 *      lifetime — no shared state, no cross-path leakage.
 *
 *   3. **Symmetry**. Every other drawer (background, spacing, css, svg)
 *      lives in its own module and returns a teardown. Form follows the
 *      same pattern; the property-panel's `_renderSectionDrawerBody`
 *      becomes uniformly thin.
 */

import { FormBuilder } from '../form-builder.js';

/**
 * Mount the Form drawer body.
 *
 * @param {HTMLElement} container - drawer body element
 * @param {{ instanceId: string }} ctx
 * @returns {() => void} teardown that destroys the FormBuilder cleanly
 */
export function renderFormDrawer(container, { instanceId }) {
  if (!container || !instanceId) {
    console.warn('[form-drawer] missing container/instanceId');
    return () => {};
  }

  // Wrap in a thin section so future polish (e.g. a small "Form fields" sub-
  // header inside the drawer body, or a footer toolbar) has a stable mount
  // point without touching FormBuilder itself.
  const wrap = document.createElement('div');
  wrap.className = 'pb-spi-drawer-section pb-spi-form-drawer';
  container.appendChild(wrap);

  let formBuilder = new FormBuilder(wrap, instanceId);

  return function teardown() {
    if (!formBuilder) return;
    try {
      formBuilder.destroy();
    } catch (err) {
      console.error('[form-drawer] FormBuilder.destroy() threw:', err);
    }
    formBuilder = null;
    // The drawer body's innerHTML is cleared by drawer.js on swap; on close
    // the entire drawer is removed from the DOM. Our `wrap` element is
    // garbage-collected with it. No explicit removal needed.
  };
}
